import React, { useState, useEffect } from "react";
import { useI18n } from "../i18n/I18nContext";
import { C, RARITY } from "../constants/design";
import { SLOT, BOX, INAMES, SLOT_DEFS, INVENTORY_IMGS } from "../constants/items";
import {
  STD_ODDS,
  PREM_ODDS,
  PACK_STD,
  PACK_PREM,
} from "../constants/matches";
import { getStats, generateItemStats, iPower, iName } from "../engine/itemEngine";
import { SH } from "./ui/atoms";
import { CardShape } from "./ui/CardShape";
import type { Item } from "../types/game";

const RARITIES = ["commun", "rare", "epic", "ultra"] as const;
const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";
const CDN_COIN = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/CoinIcon.png";
const COSMETIC_PACK_COST = 15;

// ── Free box state ────────────────────────────────────────────────────────────

function storageKey(userName: string, kind: string) {
  return `free_box_${kind}_${userName}`;
}

function getNextMidnightMs(): number {
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.getTime();
}

function claimedUntil(userName: string, kind: "std" | "cosmetic"): number {
  const raw = localStorage.getItem(storageKey(userName, kind));
  if (!raw) return 0;
  return Number(raw);
}

function markClaimed(userName: string, kind: "std" | "cosmetic") {
  const until =
    kind === "std"
      ? getNextMidnightMs()
      : Date.now() + 7 * 24 * 60 * 60 * 1000;
  localStorage.setItem(storageKey(userName, kind), String(until));
}

// Level-based free (Premium): free at every multiple of `step` levels
function premiumLevelKey(userName: string) {
  return `free_box_premium_milestone_${userName}`;
}
function lastClaimedMilestone(userName: string): number {
  return Number(localStorage.getItem(premiumLevelKey(userName)) || "0");
}
function markPremiumMilestoneClaimed(userName: string, milestone: number) {
  localStorage.setItem(premiumLevelKey(userName), String(milestone));
}

function useCountdown(until: number) {
  const [ms, setMs] = useState(() => Math.max(0, until - Date.now()));
  useEffect(() => {
    if (until <= Date.now()) { setMs(0); return; }
    const id = setInterval(() => {
      setMs(Math.max(0, until - Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [until]);
  return ms;
}

function formatTime(ms: number): string {
  if (ms <= 0) return "";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h${m}m`;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

// ── Big box card (full-width) ─────────────────────────────────────────────────

type FreeConfig =
  | { type: "daily"; freeKind: "std" | "cosmetic" }
  | { type: "level"; step: number; currentLevel: number };

function BoxCard({
  title,
  subtitle,
  boxImg,
  accent,
  accentBg,
  costNode,
  canBuy,
  onBuy,
  userName,
  free,
  onFree,
}: {
  title: string;
  subtitle: string;
  boxImg: string;
  accent: string;
  accentBg: string;
  costNode: React.ReactNode;
  canBuy: boolean;
  onBuy: () => void;
  userName: string;
  free?: FreeConfig;
  onFree?: () => void;
}) {
  const { t } = useI18n();

  // Daily free state
  const [until, setUntil] = useState(() =>
    free?.type === "daily" ? claimedUntil(userName, free.freeKind) : 0
  );
  const remaining = useCountdown(until);

  // Level free state
  const [claimedMs, setClaimedMs] = useState(() =>
    free?.type === "level" ? lastClaimedMilestone(userName) : 0
  );

  let freeAvailable = false;
  let stripContent: React.ReactNode = null;

  if (!free) {
    // no free strip
  } else if (free.type === "daily") {
    freeAvailable = remaining === 0;
    const handleFree = () => {
      markClaimed(userName, free.freeKind);
      setUntil(free.freeKind === "std" ? getNextMidnightMs() : Date.now() + 7 * 24 * 60 * 60 * 1000);
      onFree?.();
    };
    stripContent = freeAvailable ? (
      <span
        onClick={handleFree}
        style={{ fontSize: 13, fontWeight: 800, color: "#4ade80", cursor: "pointer" }}
      >
        {t("shop.freeAvailable")}
      </span>
    ) : (
      <span style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.4)" }}>
        {t("shop.nextFreeIn", formatTime(remaining))}
      </span>
    );
  } else {
    const step = free.step;
    const lvl = free.currentLevel;
    const currentMilestone = Math.floor(lvl / step) * step;
    freeAvailable = currentMilestone >= step && currentMilestone > claimedMs;
    const nextMilestone = freeAvailable ? currentMilestone : currentMilestone + step;
    const handleFree = () => {
      markPremiumMilestoneClaimed(userName, currentMilestone);
      setClaimedMs(currentMilestone);
      onFree?.();
    };
    stripContent = freeAvailable ? (
      <span
        onClick={handleFree}
        style={{ fontSize: 13, fontWeight: 800, color: "#4ade80", cursor: "pointer" }}
      >
        {t("shop.freeAtLevel", currentMilestone)}
      </span>
    ) : (
      <span style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.4)" }}>
        {t("shop.nextFreeAtLevel", nextMilestone)}
      </span>
    );
  }

  return (
    <div
      style={{
        borderRadius: 18,
        overflow: "hidden",
        background: accentBg,
        border: `1px solid ${accent}40`,
        marginBottom: 12,
        position: "relative",
      }}
    >
      {/* Main body */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          padding: "18px 16px 16px",
          minHeight: 140,
          position: "relative",
        }}
      >
        {/* Left content */}
        <div style={{ flex: 1, zIndex: 2 }}>
          {/* Price badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "rgba(0,0,0,0.45)",
              borderRadius: 999,
              padding: "4px 10px 4px 8px",
              marginBottom: 10,
            }}
          >
            {costNode}
          </div>

          <div
            style={{
              fontWeight: 900,
              fontSize: 20,
              color: "#fff",
              lineHeight: 1.1,
              marginBottom: 4,
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.65)",
              fontWeight: 500,
            }}
          >
            {subtitle}
          </div>

          {/* Buy button */}
          <button
            onClick={onBuy}
            disabled={!canBuy}
            style={{
              marginTop: 12,
              border: "none",
              borderRadius: 10,
              padding: "8px 18px",
              fontWeight: 800,
              fontSize: 13,
              background: canBuy
                ? `linear-gradient(135deg, ${accent}, ${accent}bb)`
                : "rgba(255,255,255,0.1)",
              color: canBuy ? "#06121e" : "rgba(255,255,255,0.3)",
              cursor: canBuy ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t("shop.openButton")}
          </button>
        </div>

        {/* Box image — right side */}
        <img
          src={boxImg}
          alt=""
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 160,
            height: 160,
            objectFit: "contain",
            filter: `drop-shadow(0 8px 30px ${accent}99)`,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Bottom strip */}
      {stripContent != null && (
        <div
          style={{
            background: "rgba(0,0,0,0.35)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {stripContent}
        </div>
      )}
    </div>
  );
}

// ── Admin drop panel ──────────────────────────────────────────────────────────

const ALL_ITEMS: { slot: string; rarity: string; name: string }[] = [];
for (const slot of SLOT_DEFS.map((s) => s.key)) {
  for (const rar of RARITIES) {
    for (const name of INAMES[slot]?.[rar] ?? []) {
      ALL_ITEMS.push({ slot, rarity: rar, name });
    }
  }
}

function AdminDropPanel({ onDrop }: { onDrop: (item: Item) => void }) {
  const { t } = useI18n();
  const [selected, setSelected] = useState(
    ALL_ITEMS[0].name + "|" + ALL_ITEMS[0].slot + "|" + ALL_ITEMS[0].rarity
  );

  const handleDrop = () => {
    const [name, slot, rarity] = selected.split("|");
    const stats = generateItemStats(slot, rarity, 10);
    const item: Item = {
      iid: "i_" + Math.random().toString(36).slice(2, 10),
      slot: slot as Item["slot"],
      rarity: rarity as Item["rarity"],
      name,
      stats,
    };
    onDrop(item);
  };

  return (
    <div
      style={{
        background: "rgba(239,68,68,0.08)",
        border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: 14,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 900, color: "#f87171", letterSpacing: 1, textTransform: "uppercase" }}>
        {t("shop.adminTitle")}
      </div>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        style={{
          width: "100%", padding: "8px 10px",
          background: C.surface2, color: C.text,
          border: "1px solid rgba(239,68,68,0.4)", borderRadius: 9,
          fontSize: 13, fontWeight: 700,
        }}
      >
        {SLOT_DEFS.map((slotDef) => (
          <optgroup key={slotDef.key} label={slotDef.label}>
            {RARITIES.flatMap((rar) =>
              (INAMES[slotDef.key]?.[rar] ?? []).map((name) => (
                <option key={name + slotDef.key} value={`${name}|${slotDef.key}|${rar}`}>
                  [{rar}] {name}
                </option>
              ))
            )}
          </optgroup>
        ))}
      </select>
      <button
        onClick={handleDrop}
        style={{
          background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.5)",
          borderRadius: 9, padding: "9px 0", color: "#f87171",
          fontWeight: 900, fontSize: 13, cursor: "pointer",
        }}
      >
        {t("shop.adminDropButton")}
      </button>
    </div>
  );
}

// ── Main Packs component ──────────────────────────────────────────────────────

export function Packs({
  me,
  derived,
  onBuy,
  onClaimFree,
  tickets = 0,
  level = 0,
  onBuyCosmeticPack,
  isAdmin = false,
  onAdminDrop,
  inventoryFull = false,
}: {
  me?: { name?: string } | null;
  derived: { wallet: number };
  onBuy: (kind: string) => void;
  onClaimFree?: (kind: string) => void;
  tickets?: number;
  level?: number;
  onBuyCosmeticPack?: () => void;
  isAdmin?: boolean;
  onAdminDrop?: (item: Item) => void;
  inventoryFull?: boolean;
}) {
  const { t } = useI18n();
  const userName = (me as any)?.name ?? "guest";



  return (
    <div>
      <SH title={t("shop.title")} sub={t("shop.balanceLabel", derived.wallet)} />

      {inventoryFull && (
        <div
          style={{
            margin: "0 2px 12px",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(249,226,175,0.1)",
            border: "1px solid rgba(249,226,175,0.4)",
            color: "#f9e2af",
            fontSize: 12.5,
            fontWeight: 700,
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          📦 Inventory full — sell an item in your profile to buy more. Free rewards are kept safe until you make room.
        </div>
      )}

      {isAdmin && onAdminDrop && <AdminDropPanel onDrop={onAdminDrop} />}

      {/* Standard Item Box */}
      <BoxCard
        title={t("shop.standardBoxTitle")}
        subtitle={t("shop.standardBoxSubtitle")}
        boxImg={BOX.std}
        accent="#3DA9FF"
        accentBg="linear-gradient(135deg, #0d2a40 0%, #0a1e30 60%, #061422 100%)"
        costNode={
          <>
            <img src={CDN_COIN} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
            <span style={{ fontWeight: 900, fontSize: 14, color: "#fff" }}>{PACK_STD}</span>
          </>
        }
        canBuy={derived.wallet >= PACK_STD && !inventoryFull}
        onBuy={() => onBuy("std")}
        userName={userName}
        free={{ type: "daily", freeKind: "std" }}
        onFree={() => onClaimFree?.("std")}
      />

      {/* Premium Box — free every 5 levels */}
      <BoxCard
        title={t("shop.premiumBoxTitle")}
        subtitle={t("shop.premiumBoxSubtitle")}
        boxImg={BOX.premium}
        accent={C.gold}
        accentBg="linear-gradient(135deg, #2a1f00 0%, #1e1500 60%, #140e00 100%)"
        costNode={
          <>
            <img src={CDN_COIN} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
            <span style={{ fontWeight: 900, fontSize: 14, color: "#fff" }}>{PACK_PREM}</span>
          </>
        }
        canBuy={derived.wallet >= PACK_PREM && !inventoryFull}
        onBuy={() => onBuy("premium")}
        userName={userName}
        free={{ type: "level", step: 5, currentLevel: level }}
        onFree={() => onClaimFree?.("premium")}
      />

      {/* Cosmetic Box */}
      <BoxCard
        title={t("shop.cosmeticBoxTitle")}
        subtitle={t("shop.cosmeticBoxSubtitle")}
        boxImg={BOX.familier}
        accent="#a855f7"
        accentBg="linear-gradient(135deg, #2a0d40 0%, #1e0a30 60%, #140622 100%)"
        costNode={
          <>
            <img src={TICKET_ICON} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
            <span style={{ fontWeight: 900, fontSize: 14, color: "#fff" }}>{COSMETIC_PACK_COST}</span>
          </>
        }
        canBuy={tickets >= COSMETIC_PACK_COST}
        onBuy={() => onBuyCosmeticPack?.()}
        userName={userName}
      />

      <p style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5, margin: "4px 2px 0" }}>
        {t("shop.footerNote")}
      </p>
    </div>
  );
}

// ── RevealModal ───────────────────────────────────────────────────────────────

export function RevealModal({
  items,
  kind,
  equippedBySlot = {},
  onClose,
}: {
  items: Item[];
  kind: string;
  equippedBySlot?: Record<string, Item | undefined>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [stage, setStage] = useState("ready");
  const [showCta, setShowCta] = useState(false);
  const glow = kind === "premium" ? C.gold : "#3DA9FF";

  const open = () => {
    if (stage !== "ready") return;
    setStage("shaking");
    // Le coffre tremble, devient tout blanc shiny puis se compresse (squash cartoon)
    setTimeout(() => setStage("squash"), 850);
    // Au ressort de la compression, les cartes jaillissent
    setTimeout(() => setStage("cards"), 850 + 700);
    setTimeout(() => setShowCta(true), 850 + 700 + items.length * 300 + 600);
  };

  return (
    <div
      onClick={() => stage === "cards" && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(5,8,14,.65)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        overflow: "hidden",
      }}
    >
      {(stage === "ready" || stage === "shaking") && (
        <div
          onClick={(e) => { e.stopPropagation(); open(); }}
          style={{ position: "relative", display: "grid", placeItems: "center", cursor: "pointer" }}
        >
          <div
            style={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: `radial-gradient(circle,${glow}55,transparent 65%)`,
              filter: "blur(6px)",
              animation: "capPulse 2.4s infinite",
              color: glow,
            }}
          />
          <img
            src={BOX[kind as keyof typeof BOX] ?? BOX.std}
            alt=""
            style={{
              width: 220,
              height: 220,
              objectFit: "contain",
              position: "relative",
              animation: stage === "shaking" ? "shake .45s ease-in-out infinite" : "bob 2.6s ease-in-out infinite",
              filter: `drop-shadow(0 12px 32px ${glow}99)`,
            }}
          />
          {stage === "ready" && (
            <div
              style={{
                position: "absolute",
                bottom: -50,
                fontSize: 14,
                fontWeight: 800,
                color: C.text,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
           <span style={{ animation: "blink 1.4s infinite", fontWeight: 700 }}>
  {t("shop.tapToOpen")}
</span>
            </div>
          )}
        </div>
      )}

      {stage === "squash" && (
        <div style={{ position: "relative", width: 300, height: 300, display: "grid", placeItems: "center" }}>
          {/* Halo blanc qui s'intensifie pendant la compression */}
          <div
            style={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: `radial-gradient(circle,#fff,${glow} 40%,transparent 72%)`,
              animation: "burst .7s ease-out forwards",
            }}
          />
          {/* Le coffre devient tout blanc shiny et se compresse sur lui-même */}
          <img
            src={BOX[kind as keyof typeof BOX] ?? BOX.std}
            alt=""
            style={{
              width: 220,
              height: 220,
              objectFit: "contain",
              position: "relative",
              transformOrigin: "center bottom",
              animation:
                "chestSquash .7s cubic-bezier(.45,-0.4,.3,1.4) forwards, chestWhiten .7s ease-in forwards",
            }}
          />
        </div>
      )}

      {stage === "cards" && (
        <>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", perspective: 1000, animation: "springOut .55s cubic-bezier(.34,1.56,.64,1) both" }}>
            {items.map((it, i) => (
              <RCard
                key={it.iid}
                it={it}
                delay={i * 0.3}
                equippedInSlot={equippedBySlot[it.slot]}
              />
            ))}
          </div>
          <button
            onClick={onClose}
            style={{
              marginTop: 28,
              opacity: showCta ? 1 : 0,
              transition: "opacity .4s",
              pointerEvents: showCta ? "auto" : "none",
background: "#48FF85",              color: "#000000",
              border: "none",
              borderRadius: 12,
              padding: "12px 44px",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {t("shop.revealContinue")}
          </button>
        </>
      )}
    </div>
  );
}

// Card dimensions — proportional to your NEW SVG viewBox 266×377
const CARD_W = 256; // 320 - 20%
const CARD_H = Math.round(CARD_W * (377 / 266)); // ≈ 363

export function RCard({
  it,
  delay,
  equippedInSlot,
}: {
  it: Item;
  delay: number;
  equippedInSlot?: Item;
}) {
  const r = RARITY[it.rarity];
  const name = iName(it);
  const img = INVENTORY_IMGS[name];
  const isUpgrade = equippedInSlot ? iPower(it) > iPower(equippedInSlot) : !equippedInSlot;

  const faceBase: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
  };

  return (
    <div style={{ position: "relative", width: CARD_W, height: CARD_H }}>
      {/* Halo lumineux d'ambiance derrière la carte (optionnel, hérité du code de Claude) */}
      <div
        style={{
          position: "absolute",
          inset: -16,
          borderRadius: 30,
          background: `radial-gradient(circle, ${r.glow}, transparent 68%)`,
          filter: "blur(10px)",
          opacity: 0,
          animation: `glowFd .6s ${delay + 0.5}s forwards`,
          color: r.color,
        }}
      />

      {/* Wrapper de l'animation de flip */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          animation: `flipIn .75s cubic-bezier(.2,.7,.3,1) ${delay}s both`,
        }}
      >
{/* ── Dos de la carte ── */}
        <div
          style={{
            ...faceBase,
            transform: "rotateY(180deg)",
            background: `linear-gradient(160deg, ${C.surface2}, ${C.surface})`,
            border: `2px solid ${C.line}`,
            borderRadius: 18,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 34, opacity: 0.35 }}>⚽</span>
          <span className="d" style={{ fontSize: 11, fontWeight: 900, color: C.faint }}>PronoCup</span>
        </div>

        {/* ── Face avant de la carte ── */}
        <div
          style={{
            ...faceBase,
            overflow: "visible",
          }}
        >
          {/* Ton nouveau SVG de fond */}
          <div style={{ position: "absolute", inset: 0 }}>
            <CardShape rarity={it.rarity} uid={it.iid} />
          </div>
{/* Conteneur absolu calé sur la carte */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "0px 20px",
            }}
          >
{/* Nouvelle flèche d'upgrade Figma */}
            {isUpgrade && (
              <div style={{ position: "absolute", top: 24, right: 24, animation: "blink 1.4s infinite" }}>
                <svg 
                  width="20" 
                  height="25" 
                  viewBox="0 0 20 25" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#upgradeArrowGlow)">
                    <path 
                      d="M9.22168 0.31543C9.61668 -0.105146 10.2848 -0.105238 10.6797 0.31543L19.6289 9.85449C20.2277 10.4931 19.7749 11.5381 18.8994 11.5381H14.3252L15.3096 19.4141C15.3842 20.0109 14.9189 20.5381 14.3174 20.5381H5.58301C4.98166 20.5379 4.51623 20.0108 4.59082 19.4141L5.5752 11.5381H1.00195C0.12637 11.5381 -0.326543 10.4931 0.272461 9.85449L9.22168 0.31543Z" 
                      fill="#00FF55"
                    />
                  </g>

                </svg>
              </div>
            )}

            {/* 1. Le PNG : Calé à 50px du haut de la carte */}
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                width: 154, 
                height: 154,
                marginTop: 50, // Fixe le début du PNG à 50px du top
              }}
            >
              {img ? (
                <img
                  src={img}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter: `drop-shadow(0 4px 14px ${r.glow}80)`,
                  }}
                />
              ) : (
                <span style={{ fontSize: 42 }}>{(SLOT[it.slot] as any)?.emoji ?? "?"}</span>
              )}
            </div>

            {/* 2. Le Bloc Texte : Placé à exactement 20px en dessous du PNG */}
            <div 
              style={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                gap: 10, 
                width: "100%",
                marginTop: 20, // 20px de marge sous le conteneur du PNG
              }}
            >
              {/* Badge Rareté */}
              <div
                style={{
                  // Utilise une couleur fixe ou une condition si tu veux matcher Figma directement
                  background: it.rarity === "epic" ? "#FF2DEE" : r.color, 
                  color: "#000",
                  fontWeight: 900,
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  borderRadius: 999,
                  padding: "4px 12px",
                }}
              >
                {r.label}
              </div>
              
              {/* Titre en 22px */}
              <div
                style={{
                  fontWeight: 900,
                  fontSize: 22,
                  color: "#fff",
                  lineHeight: 1.1,
                  textAlign: "center",
                  wordBreak: "break-word",
                }}
              >
                {name}
              </div>
            </div>
          </div>
          {/* Fin du bloc contenu */}
          
        </div>
      </div>
    </div>
  );
}

// ── PackCard (kept for backward compat) ───────────────────────────────────────

export function PackCard({
  kind,
  cost,
  count,
  odds,
  wallet,
  onBuy,
}: {
  kind: string;
  cost: number;
  count: number;
  odds: [string, number][];
  wallet: number;
  onBuy: () => void;
}) {
  const afford = wallet >= cost;
  let packName = "Standard Pack";
  let acc = "#3DA9FF";
  let subText = `${count} item`;
  let isPremiumEffect = false;

  if (kind === "premium") { packName = "Premium Pack"; acc = C.gold; subText = `${count} item · better odds`; isPremiumEffect = true; }
  else if (kind === "familier") { packName = "Companion Pack"; acc = "#EC4899"; subText = `${count} Companion 🐾`; isPremiumEffect = true; }

  return (
    <div
      style={{
        borderRadius: 16,
        padding: "12px 12px 13px",
        background: `radial-gradient(120% 90% at 50% 0%,${acc}18,${C.surface})`,
        border: `1px solid ${isPremiumEffect ? `${acc}55` : C.line}`,
      }}
    >
      <div style={{ height: 128, display: "grid", placeItems: "center", marginBottom: 6 }}>
        <img
          src={BOX[kind as keyof typeof BOX]}
          alt=""
          style={{ width: 128, height: 128, objectFit: "contain", filter: `drop-shadow(0 8px 20px ${acc}77)` }}
        />
      </div>
      <div className="d" style={{ fontWeight: 900, fontSize: 15, color: C.text }}>{packName}</div>
      <div style={{ fontSize: 11, color: C.muted, margin: "2px 0 8px" }}>{subText}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 11 }}>
        {odds.map(([r, w]) => (
          <span key={r} style={{ fontSize: 9.5, fontWeight: 800, color: RARITY[r as keyof typeof RARITY].color }}>
            {w}%
          </span>
        ))}
      </div>
      <button
        onClick={onBuy}
        disabled={!afford}
        style={{
          width: "100%", border: "none", borderRadius: 10, padding: "9px",
          fontWeight: 900, fontSize: 13,
          background: afford ? `linear-gradient(135deg,${acc},${kind === "premium" ? "#c98f00" : kind === "familier" ? "#be185d" : "#1f78c2"})` : C.bg2,
          color: afford ? "#06121e" : C.faint,
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          cursor: afford ? "pointer" : "not-allowed",
        }}
      >
        <img src={CDN_COIN} alt="" style={{ width: 14, height: 14, objectFit: "contain" }} /> {cost}
      </button>
    </div>
  );
}
